import React, { useState, useEffect } from 'react';
import LORManagerSelector from './LORManagerSelector';
import Icon from '../../components/Icon';
const FLAG_CLASSES = { bye: 'bye', out: 'out', doubtful: 'out', questionable: 'q' };
const MOVE_ICONS   = { up: '↑', down: '↓', flat: '→' };

export default function LORWeeklyIntel({ api }) {
  const [rosterId,    setRosterId]    = useState(null);
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [draftStyle,  setDraftStyle]  = useState(null);
  const [styleLoad,   setStyleLoad]   = useState(false);

  useEffect(() => {
    if (!rosterId) return;
    setLoading(true);
    setError(null);
    fetch(`${api}/api/lor/intel/weekly/${rosterId}`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(err => {
        setError(err.message === '403'
          ? 'Weekly intel is not yet available — the LOR season hasn\'t started.'
          : 'Could not load Weekly Intel.');
        setLoading(false);
      });
  }, [api, rosterId]);

  useEffect(() => {
    if (!rosterId) return;
    setStyleLoad(true);
    setDraftStyle(null);
    fetch(`${api}/api/lor/mock-draft/tendencies/${rosterId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.profile) setDraftStyle(d); })
      .catch(() => {})
      .finally(() => setStyleLoad(false));
  }, [api, rosterId]);

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="⚡" size={18} /> Weekly Intel</h2>
        <LORManagerSelector api={api} onSelect={setRosterId} selectedRosterId={rosterId} />
      </div>

      {!rosterId && (
        <div className="lor-intel-empty">
          <div className="lor-intel-empty-icon"><Icon emoji="⚡" size={28} /></div>
          <p>Select your team to unlock your personalized weekly command center.</p>
        </div>
      )}

      {rosterId && loading && <div className="lor-loading">Building your weekly intel…</div>}
      {rosterId && error && <div className="lor-empty">{error}</div>}

      {data && !loading && <WeeklyIntelContent data={data} />}

      {rosterId && (styleLoad || draftStyle) && (
        <DraftStyleProfile profile={draftStyle?.profile} loading={styleLoad} />
      )}
    </div>
  );
}

function WeeklyIntelContent({ data }) {
  const { current_week, display_name, wins, losses, lineup, matchup, playoff_race, top_waiver_target, bye_alert, key_action } = data;

  return (
    <div className="lor-intel-content">
      <div className="lor-intel-meta">
        Week {current_week} · {display_name} · {wins}–{losses}
        {' '}· <span className="lor-generated-badge">rules-based</span>
      </div>

      {/* Key action item — always top */}
      {key_action && (
        <div className={`lor-intel-action lor-intel-action--${key_action.priority}`}>
          <span className="lor-intel-action-icon">{key_action.icon}</span>
          <div>
            <div className="lor-intel-action-label">This week's priority</div>
            <div className="lor-intel-action-text">{key_action.text}</div>
          </div>
        </div>
      )}

      <div className="lor-intel-grid">
        {/* Lineup card */}
        <div className="lor-card lor-intel-card">
          <div className="lor-card-title"><Icon emoji="📋" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Current Lineup</div>
          <div className="lor-intel-lineup">
            {(lineup || []).map((p, i) => (
              <LineupRow key={i} player={p} />
            ))}
          </div>
        </div>

        {/* Matchup card */}
        {matchup && (
          <div className="lor-card lor-intel-card">
            <div className="lor-card-title"><Icon emoji="⚔️" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> This Week's Matchup</div>
            <div className="lor-intel-matchup">
              <div className="lor-intel-matchup-scores">
                <div className="lor-intel-matchup-me">
                  <div className="lor-intel-matchup-label">You</div>
                  <div className="lor-intel-matchup-proj">{matchup.my_projected}</div>
                </div>
                <div className="lor-intel-matchup-vs">vs</div>
                <div className="lor-intel-matchup-opp">
                  <div className="lor-intel-matchup-label">{matchup.opponent_display_name}</div>
                  <div className="lor-intel-matchup-proj">{matchup.opp_projected}</div>
                </div>
              </div>
              <div className="lor-intel-matchup-verdict">
                {matchup.projected_winner === 'me'
                  ? <span className="lor-intel-verdict-win">Projected WIN by {Math.abs(matchup.projected_margin)} pts</span>
                  : <span className="lor-intel-verdict-loss">Projected LOSS by {Math.abs(matchup.projected_margin)} pts</span>
                }
                <span className="lor-intel-matchup-conf">{matchup.confidence} confidence</span>
              </div>
            </div>
          </div>
        )}

        {/* Playoff race card */}
        <div className="lor-card lor-intel-card">
          <div className="lor-card-title"><Icon emoji="🏆" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Playoff Race</div>
          {playoff_race && (
            <div className="lor-intel-playoff">
              <div className="lor-intel-playoff-pos">
                <span className="lor-intel-playoff-rank">#{playoff_race.rank}</span>
                <span className="lor-intel-playoff-label">in standings</span>
                <span className={`lor-intel-playoff-move lor-intel-playoff-move--${playoff_race.movement_dir}`}>
                  {MOVE_ICONS[playoff_race.movement_dir] || '→'}
                  {playoff_race.movement_dir !== 'flat' && ` ${Math.abs(playoff_race.movement || 0).toFixed(1)}%`}
                </span>
              </div>
              <div className="lor-intel-playoff-odds-bar-wrap">
                <div
                  className={`lor-odds-bar${playoff_race.in_playoff_position ? ' lor-odds-bar--in' : ''}`}
                  style={{ width: `${Math.min(100, playoff_race.playoff_pct || 0)}%` }}
                />
              </div>
              <div className="lor-intel-playoff-pct">
                <strong>{(playoff_race.playoff_pct || 0).toFixed(1)}%</strong> playoff odds
              </div>
              {playoff_race.in_playoff_position
                ? <div className="lor-intel-in-playoff"><Icon emoji="✓" size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Currently in playoff position</div>
                : <div className="lor-intel-out-playoff">Outside playoff cutline</div>
              }
            </div>
          )}
        </div>

        {/* Waiver target card */}
        {top_waiver_target && (
          <div className="lor-card lor-intel-card">
            <div className="lor-card-title">🚨 Top Waiver Target</div>
            <div className="lor-intel-waiver">
              <div className="lor-intel-waiver-player">
                <div className="lor-intel-waiver-name">{top_waiver_target.full_name}</div>
                <div className="lor-intel-waiver-meta">{top_waiver_target.position} · {top_waiver_target.team}</div>
              </div>
              <div className="lor-intel-waiver-stats">
                <div className="lor-intel-waiver-proj">{top_waiver_target.projected} pts projected</div>
                {top_waiver_target.faab_suggestion && (
                  <div className="lor-intel-waiver-faab">FAAB: {top_waiver_target.faab_suggestion}</div>
                )}
                <div className="lor-intel-waiver-conf">{top_waiver_target.confidence} confidence</div>
              </div>
              <div className="lor-intel-waiver-reason">{top_waiver_target.reasoning}</div>
            </div>
          </div>
        )}

        {/* Bye alert card */}
        {bye_alert && (
          <div className={`lor-card lor-intel-card lor-intel-card--bye lor-intel-card--${bye_alert.severity}`}>
            <div className="lor-card-title">🗓️ Bye Week Alert</div>
            <div className="lor-intel-bye-alert">
              <div className="lor-intel-bye-starters">
                {bye_alert.bye_starters.map((p, i) => (
                  <div key={i} className="lor-intel-bye-player">
                    <span className="lor-intel-bye-pos">{p.position}</span>
                    <span className="lor-intel-bye-name">{p.full_name}</span>
                    <span className="lor-intel-bye-loss">−{p.projected} pts</span>
                  </div>
                ))}
              </div>
              {bye_alert.priority_adds.length > 0 && (
                <div className="lor-intel-bye-action">
                  Add from waivers: <strong>{bye_alert.priority_adds.join(', ')}</strong>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function _draftStyleLabel(profile) {
  if (!profile) return 'Unknown';
  const { early_qb_bias = 0, rb_heavy_early, wr_heavy_early, te_premium, risk_profile, adp_avg_deviation = 0, adp_std_deviation = 3 } = profile;
  if (early_qb_bias > 0.30) return 'QB Early';
  if (te_premium) return 'TE Premium';
  if (rb_heavy_early && !wr_heavy_early) return 'Hero RB';
  if (wr_heavy_early && !rb_heavy_early) return 'Zero RB';
  if (risk_profile === 'value_hunter') return 'Value Hunter';
  if (risk_profile === 'upside_reach') return 'Upside Drafter';
  if (risk_profile === 'boom_bust') return 'Boom/Bust';
  if (Math.abs(adp_avg_deviation) <= 1.5 && adp_std_deviation <= 4) return 'ADP Follower';
  return 'Balanced Drafter';
}

function _adpScore(deviation, stdDev) {
  const devScore = Math.max(0, 100 - Math.abs(deviation) * 8);
  const varScore = Math.max(0, 100 - stdDev * 5);
  return Math.round((devScore + varScore) / 2);
}

function DraftStyleProfile({ profile, loading }) {
  if (loading) return (
    <div style={dtSectionStyle}>
      <div style={dtHeaderStyle}>
        <span style={dtTitleStyle}>Draft Tendencies</span>
      </div>
      <div className="lor-loading" style={{ padding: '20px 0', textAlign: 'center', color: '#666', fontSize: 13 }}>
        Analyzing draft history...
      </div>
    </div>
  );
  if (!profile) return null;

  const styleLabel  = _draftStyleLabel(profile);
  const adpScore    = _adpScore(profile.adp_avg_deviation, profile.adp_std_deviation);
  const adpColor    = adpScore >= 70 ? 'var(--green)' : adpScore >= 45 ? '#F39C12' : 'var(--red)';
  const riskColors  = { value_hunter: '#2ECC71', balanced: '#3498DB', upside_reach: '#F39C12', boom_bust: '#E74C3C' };
  const riskColor   = riskColors[profile.risk_profile] || '#95A5A6';
  const riskLabels  = { value_hunter: 'Value Hunter', balanced: 'Balanced', upside_reach: 'Upside Reach', boom_bust: 'Boom/Bust' };

  // Early round tendencies (rounds 1–4)
  const earlyItems = [
    profile.early_qb_bias > 0.20
      ? { label: 'QB', detail: `${Math.round(profile.early_qb_bias * 100)}% of early picks`, color: '#FF6B6B' }
      : null,
    profile.rb_heavy_early
      ? { label: 'RB', detail: 'heavy rotation rounds 1–4', color: '#4ECDC4' }
      : null,
    profile.wr_heavy_early
      ? { label: 'WR', detail: 'heavy rotation rounds 1–4', color: '#45B7D1' }
      : null,
    profile.te_premium
      ? { label: 'TE', detail: 'premium target rounds 1–3', color: '#F7DC6F' }
      : null,
  ].filter(Boolean);

  // Late round tendencies (rounds 14–20)
  const lateItems = [
    profile.late_handcuff_rate > 0
      ? { label: 'RB', detail: `~${profile.late_handcuff_rate.toFixed(1)} handcuffs/season`, color: '#4ECDC4' }
      : null,
  ].filter(Boolean);

  const devSign = profile.adp_avg_deviation > 0 ? '+' : '';

  return (
    <div style={dtSectionStyle}>
      <div style={dtHeaderStyle}>
        <span style={dtTitleStyle}>Draft Tendencies</span>
        {profile.years_analyzed > 0 && (
          <span style={{ fontSize: 11, color: '#555', marginLeft: 'auto' }}>
            {profile.years_analyzed} seasons · {profile.total_picks} picks analyzed
          </span>
        )}
      </div>

      {/* Style label + risk badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{
          fontSize: 14, fontWeight: 800, padding: '5px 14px', borderRadius: 20,
          background: 'rgba(var(--lor-accent-rgb),0.12)', border: '1px solid rgba(var(--lor-accent-rgb),0.3)',
          color: 'var(--lor-gold)', letterSpacing: '0.02em',
        }}>
          {styleLabel}
        </div>
        <div style={{
          fontSize: 11, fontWeight: 700, padding: '4px 11px', borderRadius: 20,
          background: riskColor + '18', color: riskColor, border: `1px solid ${riskColor}38`,
        }}>
          {riskLabels[profile.risk_profile] || profile.risk_profile}
        </div>
      </div>

      {/* Metrics row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 18 }}>

        {/* ADP Adherence */}
        <div style={dsCardStyle}>
          <div style={dsCardLabel}>ADP Adherence</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
            <span style={{ fontSize: 26, fontWeight: 800, color: adpColor, lineHeight: 1 }}>{adpScore}</span>
            <span style={{ fontSize: 11, color: '#555' }}>/100</span>
          </div>
          <div style={{ fontSize: 11, color: '#777', marginTop: 3 }}>
            Avg {devSign}{profile.adp_avg_deviation} picks from ADP
          </div>
          <div style={{ marginTop: 8, height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
            <div style={{ width: `${adpScore}%`, height: '100%', background: adpColor, borderRadius: 2, transition: 'width 0.6s ease' }} />
          </div>
        </div>

        {/* Rounds 1–4 */}
        <div style={dsCardStyle}>
          <div style={dsCardLabel}>Rounds 1–4</div>
          {earlyItems.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 2 }}>
              {earlyItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                    background: item.color + '20', color: item.color, minWidth: 28, textAlign: 'center',
                  }}>{item.label}</span>
                  <span style={{ fontSize: 11, color: '#AAA' }}>{item.detail}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Balanced — no strong positional bias</div>
          )}
        </div>

        {/* Rounds 5–13 */}
        <div style={dsCardStyle}>
          <div style={dsCardLabel}>Rounds 5–13</div>
          <div style={{ fontSize: 12, color: '#AAA', marginTop: 4, lineHeight: 1.5 }}>
            {profile.risk_profile === 'value_hunter'
              ? 'Waits for undervalued targets — patient on the board'
              : profile.risk_profile === 'upside_reach'
              ? 'Reaches for upside plays — drifts ahead of ADP'
              : profile.risk_profile === 'boom_bust'
              ? 'High-variance selections — unpredictable range'
              : 'Tracks ADP closely — consistent mid-round approach'
            }
          </div>
        </div>

        {/* Rounds 14–20 */}
        <div style={dsCardStyle}>
          <div style={dsCardLabel}>Rounds 14–20</div>
          {lateItems.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 2 }}>
              {lateItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                    background: item.color + '20', color: item.color, minWidth: 28, textAlign: 'center',
                  }}>{item.label}</span>
                  <span style={{ fontSize: 11, color: '#AAA' }}>{item.detail}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Upside/streamer approach</div>
          )}
        </div>
      </div>

      {/* Draft identity narrative */}
      {profile.description && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Draft Identity
          </div>
          <div style={{ fontSize: 13, color: '#C0C0C0', lineHeight: 1.65 }}>
            {profile.description}
          </div>
          <div style={{ fontSize: 11, color: '#444', marginTop: 10, fontStyle: 'italic' }}>
            The AI uses this profile when simulating your opponents in Realistic mode.
          </div>
        </div>
      )}
    </div>
  );
}

const dtSectionStyle = {
  marginTop: 32,
  background: 'rgba(255,255,255,0.02)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: 12,
  padding: '20px 20px 22px',
};
const dtHeaderStyle = {
  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18,
};
const dtTitleStyle = {
  fontSize: 13, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em',
};
const dsCardStyle = {
  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 8, padding: '10px 12px',
};
const dsCardLabel = { fontSize: 10, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 };

function LineupRow({ player }) {
  const hasFlag = !!player.flag;
  const flagCls = FLAG_CLASSES[player.flag] || '';

  return (
    <div className={`lor-intel-lineup-row${hasFlag ? ' lor-intel-lineup-row--flagged' : ''}`}>
      <span className="lor-intel-lineup-slot">{player.slot || player.position}</span>
      <span className="lor-intel-lineup-name">{player.full_name}</span>
      <span className="lor-intel-lineup-meta">{player.position} · {player.team}</span>
      <span className="lor-intel-lineup-proj">{player.projected}</span>
      {hasFlag && (
        <span className={`lor-intel-lineup-flag lor-intel-lineup-flag--${flagCls}`}>
          {player.flag_label}
        </span>
      )}
    </div>
  );
}
